/**
 * ArubaPlaceholder — thin "coming soon" body for the 4 Aruba pages that
 * haven't been ported yet. Renders the ArubaSubNav so navigation between
 * Aruba pages still works.
 */
import { Link } from "react-router-dom";
import { Construction } from "lucide-react";
import { Card, CardContent, CardSub, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArubaSubNav } from "@/components/app/aruba-sub-nav";

export interface ArubaPlaceholderProps {
  title: string;
  hint?: string;
}

export function ArubaPlaceholder({ title, hint }: ArubaPlaceholderProps) {
  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-2 text-xs text-fg-mute">
        <Link to="/" className="text-fg-dim hover:text-fg">Operations</Link>
        <span className="opacity-40">/</span>
        <Link to="/aruba/keys" className="text-fg-dim hover:text-fg">Aruba</Link>
        <span className="opacity-40">/</span>
        <span className="text-fg">{title}</span>
      </div>

      <ArubaSubNav />

      <div className="flex items-end justify-between gap-6">
        <div>
          <h1 className="m-0 text-3xl font-extrabold tracking-tight text-aurora">{title}</h1>
          <p className="mt-1.5 text-sm text-fg-dim">{hint ?? "This Aruba page hasn't been ported to the new frontend yet."}</p>
        </div>
        <Badge variant="warn" size="md">Coming soon</Badge>
      </div>

      <Card>
        <CardContent className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-aurora-soft text-violet">
            <Construction size={20} />
          </div>
          <div>
            <CardTitle>Migration in progress</CardTitle>
            <CardSub>Use the legacy Django page for now — bookmark this URL; it'll light up when the React port lands.</CardSub>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
